import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Github, Linkedin } from 'lucide-react';
import { Logo } from './Layout';

/**
 * SiteFooter — minimal Apple-quiet footer.
 * Renders below the main page on every route via AppShell.
 */
const LINKS_PRODUCT = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/#product', label: 'Features' },
  { to: '/#how-it-works', label: 'How it works' },
  { to: '/#faq', label: 'FAQ' },
];

const LINKS_COMPANY = [
  { to: '/methodology', label: 'Methodology' },
  { to: '/contact', label: 'Contact' },
  { to: '/support', label: 'Support' },
];

const LINKS_LEGAL = [
  { to: '/terms', label: 'Terms of Service' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/refund-policy', label: 'Refund Policy' },
];

export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="relative border-t border-border/60 bg-card/40 backdrop-blur-sm"
      aria-label="Site footer"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr]">
          {/* Brand block */}
          <div>
            <Logo />
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xs">
              Executive presence, quietly engineered. Audit your LinkedIn, generate voice-matched posts, and benchmark against the leaders in your niche.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="mailto:help@blayzrr.com"
                className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
                data-testid="footer-email"
                aria-label="Email support"
              >
                <Mail size={13} aria-hidden="true" /> help@blayzrr.com
              </a>
            </div>
            <p className="mt-5 text-[11px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground/80">
              GDPR &amp; UK PECR compliant
            </p>
          </div>

          {/* Product */}
          <FooterColumn title="Product" links={LINKS_PRODUCT} testid="footer-product" />

          {/* Company */}
          <FooterColumn title="Company" links={LINKS_COMPANY} testid="footer-company" />

          {/* Legal */}
          <FooterColumn title="Legal" links={LINKS_LEGAL} testid="footer-legal" />
        </div>

        <div className="mt-12 pt-6 border-t border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[12px] text-muted-foreground">
            &copy; {year} Blayzrr. All rights reserved. <span className="hidden sm:inline">&middot;</span> Made with care in India. <span className="hidden sm:inline">&middot;</span> An initiative of <strong className="text-foreground">DOTCONVERSE</strong>.
          </p>
          <p className="text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground/70">
            blayzrr.com
          </p>
        </div>
      </div>
    </footer>
  );
}

const FooterColumn = ({ title, links, testid }) => (
  <div data-testid={testid}>
    <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">{title}</p>
    <ul className="mt-4 space-y-2.5">
      {links.map((l) => (
        <li key={l.to}>
          {l.to.startsWith('/#') ? (
            <a
              href={l.to.slice(1)}
              className="text-sm text-foreground/85 hover:text-primary transition-colors"
            >
              {l.label}
            </a>
          ) : (
            <Link
              to={l.to}
              className="text-sm text-foreground/85 hover:text-primary transition-colors"
            >
              {l.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);
